import { useState } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { useAsyncEffect } from 'client/utils/use-async-effect';
import { AvatarStack } from '@components/avatars/avatar-stack';
import { showError } from '@components/app-error';

type RsvpSummary = RpxResponse<typeof rpx.meetingRsvps.getRsvpSummary>;

export function RsvpCard({ meetingId }: { meetingId: UUID }) {
  const [data, setData] = useState<RsvpSummary | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useAsyncEffect(async () => {
    const result = await rpx.meetingRsvps.getRsvpSummary({ meetingId });
    setData(result);
  }, [meetingId]);

  if (!data) {
    return undefined;
  }

  async function handleRsvp() {
    if (!data) {
      return;
    }
    setSaving(true);
    try {
      if (data.hasRsvped) {
        await rpx.meetingRsvps.cancelRsvp({ meetingId });
        setData({
          ...data,
          hasRsvped: false,
          totalRsvps: Math.max(0, data.totalRsvps - 1),
        });
      } else {
        await rpx.meetingRsvps.advanceRsvp({ meetingId });
        setData({
          ...data,
          hasRsvped: true,
          totalRsvps: data.totalRsvps + 1,
        });
      }
    } catch (err) {
      showError(err);
    } finally {
      setSaving(false);
    }
  }

  const count = data.totalRsvps;

  return (
    <div class="mt-6 p-5 rounded-xl border border-indigo-200/60 bg-gradient-to-br from-indigo-50/80 via-white to-violet-50/60">
      <div class="flex flex-col gap-3">
        {count > 0 && (
          <div class="flex items-center gap-3">
            <AvatarStack users={data.avatars} totalCount={count} maxDisplay={6} size="size-7" />
            <span class="text-sm text-gray-600">
              {data.hasRsvped
                ? count === 1
                  ? "You're going"
                  : `You and ${count - 1} ${count - 1 === 1 ? 'other' : 'others'} are going`
                : `${count} ${count === 1 ? 'person is' : 'people are'} going`}
            </span>
          </div>
        )}
        {count === 0 && !data.hasRsvped && (
          <p class="text-sm text-gray-500">Be the first to save your spot!</p>
        )}
        <div class="flex items-center gap-3">
          {data.hasRsvped ? (
            <button
              type="button"
              disabled={saving}
              class="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold bg-green-100 text-green-700 border border-green-200 cursor-default"
            >
              <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
              You're in!
            </button>
          ) : (
            <button
              type="button"
              disabled={saving}
              onClick={handleRsvp}
              class="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition shadow-sm"
            >
              Save my spot!
            </button>
          )}
          {data.hasRsvped && (
            <button
              type="button"
              disabled={saving}
              onClick={handleRsvp}
              class="text-xs text-gray-400 hover:text-gray-600 transition"
            >
              Cancel RSVP
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
